import type { BasicDie, DerivedStats, MasterDie, SpellId } from './types';
import { DIE_SPELLS } from '../data/dieSpells';
import { SLOT_SPELLS } from '../data/slotSpells';
import { affixValue, hasAffix, sumAffix } from './affixes';
import { carriedDieSpells, detectSpells, spellContextOf, type RolledFace } from './combos';
import { applyLoaded, dieDamageFactor, effectiveLoaded } from './dice';
import { Rng } from './rng';

/**
 * Espérance analytique des dégâts d'un lancer — « calculateur » du panneau de
 * debug (COMMENTS v0.2.1 §7) et affichage du palier de dégâts (v0.3.0 §2).
 *
 * Tout est **fermé** : aucune simulation, aucun `Rng`. Les distributions de
 * faces sont calculées exactement (affixes *Lesté* et *Avantageux* compris) et
 * la loi jointe des dés Maîtres est énumérée pour tenir compte de la
 * **corrélation** entre les faces et le sort qu'elles déclenchent (une paire de
 * 6 est à la fois le meilleur `D_L` et le meilleur `D_S`).
 *
 * Hypothèses assumées, documentées pour l'interface :
 *  - cible **sans bouclier** (aucun dé absorbé) et **au-dessus** du seuil
 *    d'exécution (`affixMult` = 1, *Exécutant* ignoré) ;
 *  - poison, *Temporel* et *Doré* hors périmètre (ce sont des effets d'application,
 *    pas des dégâts de lancer) ;
 *  - *Préparation* (v0.50.0, A100-2) comptée exactement : les sorts de dé
 *    des dés des emplacements 1 à 3 sont renforcés dans `detectSpells`
 *    (`ExpectedOptions.prepDieIds`), comme ceux d'*Arrière-garde* ;
 *  - cible ordinaire : le Mage Maudit (A100-1) n'est pas compté ;
 *  - effets d'**état de combat** hors périmètre (v0.7.0) : l'Enfer de *666*
 *    (×1,5), la séquence de *Premiers* (×2, ×3, ×5), la mutilation (×1,3) et le
 *    malus du *Larbin* (×0,75) ne sont pas comptés — l'espérance décrit le
 *    lancer **du joueur**, hors états temporaires ;
 *  - v0.49.0 (A98) : les copies de *42* (lancer suivant), la marque de *Null*,
 *    la *Tempête* et *Rouleau Compresseur* (il lui faut un lancer précédent)
 *    sont ignorés ; *Vingt-dicte* double les dés Basiques du lancer où il
 *    sort.
 *
 * Au-delà de `MAX_COMBINATIONS` = 6^5 combinaisons (4 ou 5 dés à beaucoup de
 * faces), la loi jointe des dés Maîtres est estimée par **Monte-Carlo** sur
 * `MONTE_CARLO_SAMPLES` tirages à graine fixe, au lieu d'être énumérée.
 */
export interface ExpectedThrow {
  /** Dégâts moyens d'un lancer **sans sort ni critique** : `E[D_L] × M_P × M_T`. */
  meanNoSpell: number;
  /** Plancher de `D_L × M_P × M_T` (toutes les faces à 1). */
  min: number;
  /** Plafond de `D_L × M_P × M_T` (toutes les faces au maximum). */
  max: number;
  /** Chance de critique du lancer (base + *Lancer précis* + Σ *Précis*), bornée à 1. */
  critChance: number;
  /** `D_C` appliqué en cas de critique. */
  critMultiplier: number;
  /** `meanNoSpell × (1 − p + p × D_C)`. */
  meanWithCrit: number;
  /** Probabilité de déclencher chaque sort sur un lancer. */
  spellChance: Record<SpellId, number>;
  /** Dégâts moyens complets : `E[D_L × D_S] × M_P × M_T × E[D_C]`. */
  meanWithSpells: number;
  /** Temps de retour entre deux lancers (s). */
  perThrowSeconds: number;
  /** `meanWithSpells / perThrowSeconds`. */
  dps: number;
}

/**
 * Nombre maximal de combinaisons de faces **énumérées exactement** : `6^5`
 * (7 776), soit cinq d6. Au-delà — cinq d12 en font 248 832 —, la loi jointe
 * est estimée par **Monte-Carlo** (`MONTE_CARLO_SAMPLES` tirages, graine fixe :
 * la fonction reste déterministe et sans effet de bord observable).
 */
export const MAX_COMBINATIONS = 6 ** 5;

/** Tirages de Monte-Carlo quand l'énumération exacte est hors de portée. */
export const MONTE_CARLO_SAMPLES = 10_000;

/** Graine du Monte-Carlo : `expectedThrow()` doit rendre deux fois le même nombre. */
const MONTE_CARLO_SEED = 0x5eed;

/**
 * Loi de la face d'un dé, index 0 = face 1.
 *
 * *Lesté* (`weightedChance`) remplace le tirage par la face maximale ;
 * *Avantageux* répète la procédure complète deux fois et garde le meilleur —
 * exactement `core/dice.ts : rollFace`.
 */
export function faceDistribution(
  sides: number,
  weightedChance = 0,
  advantage = false,
): number[] {
  const w = Math.min(1, Math.max(0, weightedChance));
  const single: number[] = [];
  for (let k = 1; k <= sides; k++) {
    single.push((1 - w) / sides + (k === sides ? w : 0));
  }
  if (!advantage) return single;
  // P(max = k) = F(k)² − F(k−1)²
  const out: number[] = [];
  let cum = 0;
  let prev = 0;
  for (let k = 1; k <= sides; k++) {
    cum += single[k - 1];
    out.push(cum * cum - prev * prev);
    prev = cum;
  }
  return out;
}

/** Espérance d'une loi de face (index 0 = face 1). */
export function meanOfDistribution(dist: readonly number[]): number {
  let sum = 0;
  for (let i = 0; i < dist.length; i++) sum += (i + 1) * dist[i];
  return sum;
}

/**
 * Loi de face d'un dé Maître, affixes compris — *Pipé* (A20) compris : la face
 * cible récupère la probabilité des faces qu'elle remplace.
 *
 * *Pipé* est appliqué **avant** *Avantageux* (comme `core/dice.ts`), donc la
 * loi du maximum est calculée sur les faces déjà substituées.
 */
export function masterFaceDistribution(die: MasterDie, stats: DerivedStats): number[] {
  const weighted = affixValue(die, 'weighted') + stats.masterWeightedChance;
  const advantage = affixValue(die, 'advantaged') > 0;
  const loaded = effectiveLoaded(die);
  if (!loaded) return faceDistribution(die.sides, weighted, advantage);

  // Loi d'un tirage simple : uniforme, puis substitution de *Pipé*, puis *Lesté*.
  const w = Math.min(1, Math.max(0, weighted));
  const single = new Array<number>(die.sides).fill(0);
  for (let k = 1; k <= die.sides; k++) {
    single[applyLoaded(k, loaded) - 1] += (1 - w) / die.sides;
  }
  single[die.sides - 1] += w;
  if (!advantage) return single;
  // P(max = k) = F(k)² − F(k−1)², sur la loi déjà substituée.
  const out: number[] = [];
  let cum = 0;
  let prev = 0;
  for (let k = 1; k <= die.sides; k++) {
    cum += single[k - 1];
    out.push(cum * cum - prev * prev);
    prev = cum;
  }
  return out;
}

/** Table de probabilités initialisée à 0 pour **tous** les sorts du jeu. */
export function emptySpellChance(): Record<SpellId, number> {
  const out = {} as Record<SpellId, number>;
  for (const def of SLOT_SPELLS) out[def.id] = 0;
  for (const def of DIE_SPELLS) out[def.id] = 0;
  return out;
}

/**
 * Options de l'espérance (v0.43.0, A89-6) — placement des dés, que la seule
 * liste `masters` ne dit pas.
 */
export interface ExpectedOptions {
  /** *Arrière-garde* : identifiants des dés des deux derniers emplacements équipés. */
  rearguardDieIds?: readonly string[];
  /**
   * *Préparation* (v0.50.0, A100-2) : identifiants des dés équipés dans les
   * emplacements 1 à 3 (`Game.prepSlots()`).
   */
  prepDieIds?: readonly string[];
  /**
   * *Caoutchouc* (v0.47.0, A94-16) : identifiants des dés qui **rebondissent**.
   * Leur seconde face entre dans `D_L` (niveau compris, sans dégât plat) et
   * dans les sorts ; leurs affixes ne comptent qu'une fois.
   */
  bounceDieIds?: readonly string[];
}

/**
 * Espérance analytique d'un lancer, pour les dés Maîtres `masters`, le
 * contenant `container` et les statistiques dérivées `stats`.
 *
 * *Arrière-garde* (A89-6) et *Préparation* (v0.50.0, A100-2) : les sorts
 * renforcés entrent dans `D_S` via `detectSpells`.
 *
 * Vérifiée contre une simulation de Monte-Carlo dans `tests/core/expected.test.ts`
 * (10 000 lancers, tolérance 3 %).
 */
export function expectedThrow(
  masters: readonly MasterDie[],
  container: readonly BasicDie[],
  stats: DerivedStats,
  opts: ExpectedOptions = {},
): ExpectedThrow {
  const MP = 1;
  const MT = stats.damageMult;
  const scale = MP * MT;

  // ── D_L : dés Basiques (indépendants du sort) et dégâts plats.
  let basicMean = 0;
  let basicMin = 0;
  let basicMax = 0;
  for (const die of container) {
    basicMean += (die.sides + 1) / 2;
    basicMin += 1;
    basicMax += die.sides;
  }
  const flat = stats.flatDamagePerDie * masters.length;

  /**
   * Faces lancées : les dés Maîtres, puis un second tirage de chaque dé qui
   * rebondit (*Caoutchouc*) et de chaque dé qui porte *Réplicant* (v0.49.0,
   * A98-7 : une copie par source) — même loi, même niveau, indépendants.
   */
  const bounceIds = opts.bounceDieIds ?? [];
  const rollers: MasterDie[] = masters.slice();
  for (const id of bounceIds) {
    const die = masters.find((m) => m.id === id);
    if (die) rollers.push(die);
  }
  for (const die of masters) if (hasAffix(die, 'replicant')) rollers.push(die);
  const dists = rollers.map((d) => masterFaceDistribution(d, stats));
  /**
   * Niveau des dés (A94-5) : la face d'un Maître pèse `face × 1,5^niveau` ;
   * v0.49.0 (A98-6) : × 1000 sur un dé Corrupted.
   */
  const factors = rollers.map((d) => dieDamageFactor(d, stats));
  let masterMean = 0;
  let masterMin = 0;
  let masterMax = 0;
  for (let i = 0; i < rollers.length; i++) {
    masterMean += meanOfDistribution(dists[i]) * factors[i];
    masterMin += factors[i];
    masterMax += rollers[i].sides * factors[i];
  }

  const meanDL = masterMean + basicMean + flat;
  const meanNoSpell = meanDL * scale;
  const min = (masterMin + basicMin + flat) * scale;
  const max = (masterMax + basicMax + flat) * scale;

  // ── D_C : un seul tirage pour tout le lancer.
  const critChance = Math.min(1, Math.max(0, stats.critChance + sumAffix(masters, 'precise')));
  const critMultiplier = stats.critMult + sumAffix(masters, 'incisive');
  const critFactor = 1 - critChance + critChance * critMultiplier;
  const meanWithCrit = meanNoSpell * critFactor;

  // ── D_S : énumération **exacte** de la loi jointe des faces des dés Maîtres
  // (au plus 3 emplacements, donc 216 combinaisons à 3 d6).
  const spellChance = emptySpellChance();
  const rearguardIds = opts.rearguardDieIds ?? [];
  const rearguardBoost = stats.rearguardBoost ?? 0;
  const prepIds = opts.prepDieIds ?? [];
  const prepBoost = stats.prepBoost ?? 0;
  // v0.47.0 : *Poker* et Grimoires compris (`spellContextOf`).
  const ctx = spellContextOf(stats, carriedDieSpells(masters), {
    rearguard:
      rearguardBoost > 0 && rearguardIds.length > 0
        ? {
            spells: carriedDieSpells(masters.filter((m) => rearguardIds.includes(m.id))),
            boost: rearguardBoost,
          }
        : null,
    prep:
      prepBoost > 0 && prepIds.length > 0
        ? { spells: carriedDieSpells(masters.filter((m) => prepIds.includes(m.id))), boost: prepBoost }
        : null,
  });
  const combinations = dists.reduce((n, d) => n * d.length, 1);
  /** *Vingt-dicte* (A98-4) : les dés Basiques comptent deux fois quand il sort. */
  const doubled = (outcome: { spells: readonly { id: SpellId }[] }): number =>
    outcome.spells.some((sp) => sp.id === 'twentyDict') ? 2 : 1;
  let meanDLDS = meanDL;
  if (rollers.length >= 2) {
    const rolls: RolledFace[] = rollers.map((d) => ({ face: 1, sides: d.sides }));
    if (combinations <= MAX_COMBINATIONS) {
      // ── Énumération **exacte** : jusqu'à 6^5 = 7 776 combinaisons.
      meanDLDS = 0;
      const walk = (index: number, probability: number, faceSum: number): void => {
        if (index === rollers.length) {
          const outcome = detectSpells(rolls, ctx);
          for (const spell of outcome.spells) spellChance[spell.id] += probability;
          meanDLDS += probability * (faceSum + basicMean * doubled(outcome) + flat) * outcome.multiplier;
          return;
        }
        const dist = dists[index];
        for (let k = 0; k < dist.length; k++) {
          if (dist[k] <= 0) continue;
          rolls[index].face = k + 1;
          walk(index + 1, probability * dist[k], faceSum + (k + 1) * factors[index]);
        }
      };
      walk(0, 1, 0);
    } else {
      // ── **Monte-Carlo** (v0.7.0) : 4 ou 5 dés Maîtres à beaucoup de faces
      // (5 d12 = 248 832 combinaisons, deux d100 = 10 000) sortent du budget
      // de l'énumération. Les lois marginales `dists` sont indépendantes : on
      // tire chaque dé dans sa propre loi et on moyenne. Graine fixe → résultat
      // reproductible ; l'erreur relative attendue est de l'ordre du pour-cent.
      const rng = new Rng(MONTE_CARLO_SEED);
      const cumulative = dists.map((dist) => {
        const cum: number[] = [];
        let acc = 0;
        for (const p of dist) cum.push((acc += p));
        return cum;
      });
      const draw = (index: number): number => {
        const cum = cumulative[index];
        const u = rng.next();
        for (let k = 0; k < cum.length; k++) if (u <= cum[k]) return k + 1;
        return cum.length;
      };
      let sum = 0;
      for (let n = 0; n < MONTE_CARLO_SAMPLES; n++) {
        let faceSum = 0;
        for (let i = 0; i < rollers.length; i++) {
          const face = draw(i);
          rolls[i].face = face;
          faceSum += face * factors[i];
        }
        const outcome = detectSpells(rolls, ctx);
        for (const spell of outcome.spells) spellChance[spell.id] += 1 / MONTE_CARLO_SAMPLES;
        sum += (faceSum + basicMean * doubled(outcome) + flat) * outcome.multiplier;
      }
      meanDLDS = sum / MONTE_CARLO_SAMPLES;
    }
  }

  const meanWithSpells = meanDLDS * scale * critFactor;
  const perThrowSeconds = stats.throwCooldown;
  return {
    meanNoSpell,
    min,
    max,
    critChance,
    critMultiplier,
    meanWithCrit,
    spellChance,
    meanWithSpells,
    perThrowSeconds,
    dps: perThrowSeconds > 0 ? meanWithSpells / perThrowSeconds : 0,
  };
}
